import { useTranslation } from "react-i18next";
import type { AskedInput, StepPlan } from "../../../api/content/stepPlans";
import type { StepDisplayState } from "../../../api/threads/liveMessages";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import PlannedInputsList, { type GivenValues } from "./PlannedInputsList";
import { namesText, reasonText, type StepNameOf } from "./planText";

/**
 * What a step it waits for is doing in this Run, as module C shows it (W-22), where that says
 * more than its plan (TR-B1-02): a step requested, waiting or failed is not one that "can run
 * now", whatever its plan says. The other states leave the words to the plan.
 */
const PRODUCER_WORD: Partial<Readonly<Record<StepDisplayState, string>>> = {
  needs_input: "needs",
  input: "input",
  approval: "waitingForApproval",
  offline: "offline",
  slot: "waiting",
  queued: "queued",
  running: "running",
  failed: "failed",
};

interface StepReadinessProps {
  plan: StepPlan;
  /** Every step's plan by key: what a step it waits for needs. */
  plans: ReadonlyMap<string, StepPlan>;
  stepName: StepNameOf;
  /** Values given here for the asked inputs; `null` where none can be given. */
  values: GivenValues | null;
  onChange: (values: GivenValues) => void;
  /** The key a value for an asked input is sent under; its destination by default. */
  keyOf?: (input: AskedInput) => string;
  /**
   * `reference`: where every step's own inputs are on the same form (the start), an asked
   * input is named with the step whose field fills it, never edited twice (OW-04).
   */
  asked?: "editors" | "reference";
  /** Each step it waits for as module C shows it in this Run; `null` where only its plan says it (the start). */
  stateOf?: (stepKey: string) => StepDisplayState | null;
}

/**
 * Whether a step can run now (Johannes, 27 Oct, point 8): what it still needs, which steps it
 * waits for and whether each of those can run, and, asked here, what those steps lack through
 * the chain. A value given for an asked input goes to the step that takes it; the wiring
 * passes the rest on. Module B decides all of it (ADR-0032 D7); this words it. Where the
 * steps asked for are on the same form, the asked inputs point to their one field there.
 */
export default function StepReadiness({ plan, plans, stepName, values, onChange, keyOf = (input) => input.destination, asked = "editors", stateOf = () => null }: StepReadinessProps) {
  const { t } = useTranslation();
  const labelOf = (name: string) => plan.inputs.find((input) => input.name === name)?.label ?? name;
  const byStep = groupByStep(plan.asked);
  return (
    <div className="space-y-1.5">
      {plan.reasons.length === 0 ? null : (
        <p className={FORM_HELP_TEXT_CLASS}>{plan.reasons.map((reason) => reasonText(reason, t, labelOf)).join(". ")}.</p>
      )}
      {plan.waitsFor.length === 0 ? null : (
        <div className={FORM_HELP_TEXT_CLASS}>
          <p>{t("threads.plan.waitsFor", { steps: namesText(plan.waitsFor.map(stepName), t) })}</p>
          <ul className="ml-4 list-disc">
            {plan.waitsFor.map((key) => (
              <li key={key}>{producerText(plans.get(key), stateOf(key), key, t, stepName)}</li>
            ))}
          </ul>
          <p>{t("threads.plan.wiring")}</p>
        </div>
      )}
      {asked === "reference"
        ? [...byStep].map(([stepKey, inputs]) => (
            <p key={stepKey} className={FORM_HELP_TEXT_CLASS}>
              {t("threads.plan.askedAt", { step: stepName(stepKey), inputs: namesText(inputs.map((input) => input.label), t) })}
            </p>
          ))
        : [...byStep].map(([stepKey, inputs]) => (
        <section key={stepKey} className="space-y-1">
          <h4 className="text-xs font-semibold">{t("threads.plan.askedFor", { step: stepName(stepKey) })}</h4>
          <PlannedInputsList
            inputs={inputs}
            stepName={stepName}
            values={values}
            onChange={onChange}
            keyOf={keyOf}
          />
        </section>
      ))}
    </div>
  );
}

function producerText(producer: StepPlan | undefined, state: StepDisplayState | null, key: string, t: ReturnType<typeof useTranslation>["t"], stepName: StepNameOf): string {
  const step = stepName(key);
  const said = state === null ? undefined : PRODUCER_WORD[state];
  if (said !== undefined) return t(`threads.plan.producer.${said}`, { step });
  if (producer === undefined || producer.ready) return t("threads.plan.producer.ready", { step });
  if (producer.reasons.length > 0) return t("threads.plan.producer.needs", { step });
  return t("threads.plan.producer.waits", { step, steps: namesText(producer.waitsFor.map(stepName), t) });
}

function groupByStep(asked: readonly AskedInput[]): ReadonlyMap<string, readonly AskedInput[]> {
  const groups = new Map<string, AskedInput[]>();
  for (const input of asked) groups.set(input.stepKey, [...(groups.get(input.stepKey) ?? []), input]);
  return groups;
}
